'use client';
import React from 'react';
import * as Select from '@radix-ui/react-select';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { cva } from 'class-variance-authority';
import { IconMap } from '../IconMap';
import { Icon } from '../Icon';
import { Label } from '../Label';
import { cn } from '@workspace/lib';

// Matching the existing dropdown button styling
const selectTrigger = cva(
  'flex items-center w-full rounded px-3 py-2 h-11 bg-white text-left focus:outline-none font-openSans transition hover:bg-secondary-50 focus-within:border-secondary-200 focus-within:shadow-focus',
  {
    variants: {
      status: {
        default: 'border-secondary-100 text-secondary-200!',
        error: 'border-error focus-within:!border-sm !border-md',
        success: 'border-success ',
      },
    },
    defaultVariants: {
      status: 'default',
    },
  },
);

export interface SelectDropdownOption {
  id?: string;
  label: string;
  value: string;
  icon?: keyof typeof IconMap | React.ReactNode;
  disabled?: boolean;
}

export interface SelectDropdownProps {
  label: string;
  hint?: string;
  placeholder: string;
  options: SelectDropdownOption[];
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  labelClassName?: string;
  status?: 'default' | 'error' | 'success';
  onBlur?: () => void;
}

export function SelectDropdown({
  label,
  hint,
  placeholder,
  options,
  value,
  onChange,
  className,
  labelClassName,
  status = 'default',
  onBlur,
}: SelectDropdownProps) {
  const selectedOption = options.find((opt) => opt.value === value);

  const iconColorClass = {
    default: 'text-secondary-400',
    error: 'text-error group-focus-within:text-secondary-400',
    success: 'text-success',
  }[status];

  return (
    <div className={cn('w-56 relative group', className)}>
      {label && (
        <Label
          text={label}
          className={cn('text-sm font-medium text-gray-700 flex items-center gap-1', labelClassName)}
        />
      )}
      {hint && <div className="text-xs text-secondary-400 mb-1">{hint}</div>}
      <Select.Root value={value} onValueChange={onChange}>
        <Select.Trigger
          onBlur={onBlur}
          className={cn(selectTrigger({ status }), 'border w-full justify-between')}
          aria-label={label || placeholder}
        >
          <Select.Value placeholder={placeholder}>
            {selectedOption ? (
              <span className="flex items-center">
                {selectedOption.icon && <span className="mr-2">{selectedOption.icon}</span>}
                <span className="text-secondary-300">{selectedOption.label}</span>
              </span>
            ) : (
              <span className="text-secondary-200">{placeholder}</span>
            )}
          </Select.Value>
          <Select.Icon className={cn('transition-transform duration-200', iconColorClass)}>
            <Icon name="chevronDownSmallIcon" size={24} />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            className="z-50 bg-white border border-secondary-100 rounded shadow-lg w-[var(--radix-select-trigger-width)]"
            position="popper"
            sideOffset={4}
          >
            <ScrollArea.Root className="w-full h-full" type="auto">
              <Select.Viewport asChild>
                <ScrollArea.Viewport
                  className="w-full h-full max-h-60"
                  style={{ overflowX: 'hidden', overflowY: 'auto' } as React.CSSProperties}
                >
                  {options.map((opt) => (
                    <Select.Item
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.disabled}
                      className={cn(
                        'flex items-center px-3 py-2 cursor-pointer select-none transition outline-none',
                        'hover:bg-secondary-50 focus:bg-secondary-50',
                        'data-[highlighted]:bg-secondary-50',
                        opt.disabled && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      {opt.icon && (
                        <span className="mr-2">
                          {typeof opt.icon === 'string' ? (
                            <Icon name={opt.icon as keyof typeof IconMap} size={18} />
                          ) : (
                            opt.icon
                          )}
                        </span>
                      )}
                      <Select.ItemText>
                        <span className="flex-1 font-openSans">{opt.label}</span>
                      </Select.ItemText>
                      {opt.disabled && (
                        <span className="ml-2 text-secondary-400">
                          <svg width="16" height="16" fill="none" viewBox="0 0 16 16">
                            <path
                              d="M4 8a4 4 0 118 0 4 4 0 01-8 0zm8 0H4"
                              stroke="#888"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </Select.Item>
                  ))}
                </ScrollArea.Viewport>
              </Select.Viewport>
              <ScrollArea.Scrollbar orientation="vertical" className="px-1 py-2 rounded bg-white">
                <ScrollArea.Thumb className="!w-1 rounded bg-primary" />
              </ScrollArea.Scrollbar>
            </ScrollArea.Root>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
}
